const SharedUtils = require('../utils/SharedUtils');
const get = SharedUtils.getURLParams();
const BarsParser = require('./base/config/BarsParser');
const BridgeParser = require('./base/config/BridgeParser');
const GameParser = require('./base/config/GameParser');
const GambleParser = require('./base/config/GambleParser');
const BonusParser = require('./base/config/BonusParser');
const WidgetsParser = require('./base/config/WidgetsParser');
const JackpotParser = require('./base/config/JackpotParser');
const TournamentParser = require('./base/config/TournamentParser');
const BundleParser = require('./base/config/BundleParser');
const AnalyticsParser = require('./base/config/AnalyticsParser');
const ServerParser = require('./base/config/ServerParser');
const ErrorsParser = require('./base/config/ErrorsParser');

import JackpotSkinMapping from '../apps/jackpot/JackpotSkinMapping';
import TournamentSkinMapping from '../apps/tournament/tournamentSkinMapping';
import RegulationsFactory from './regulations/RegulationsFactory';
import regulationTypesImported from './regulations/regulationTypes';
import mappingFactory from '../mappings/mappingsFactory';
import brRegulationBrands from './evolution/brRegulationBrands';
// import asiaSkinMapping from '../apps/tournament/tournamentAsiaSkinMapping';


/**
 * Base class encapsulating config properties and their parse functions.
 *
 * @class
 *
 **/
class BaseConfig {
    static regulationTypes = regulationTypesImported;

    static deRegulation = [
        { name: BaseConfig.regulationTypes.TURBO },
        { name: BaseConfig.regulationTypes.TURBO_SPIN },
        { name: BaseConfig.regulationTypes.WIN_RATES },
        { name: BaseConfig.regulationTypes.MIN_ROUND_DURATION, options: { duration: 5 } },
        { name: BaseConfig.regulationTypes.MIN_ROUND_DURATION_STRICT },
        { name: BaseConfig.regulationTypes.MAX_WIN_PROBABILITY },
        { name: BaseConfig.regulationTypes.PAYTABLE_DYNAMIC_PAYOUTS },
        { name: BaseConfig.regulationTypes.MAX_MULTIPLIER_WIN_LINES },
        { name: BaseConfig.regulationTypes.SKIP_ANIMATION }
    ]

    static brRegulation = [
        { name: BaseConfig.regulationTypes.REGULATION_RTP },
        { name: BaseConfig.regulationTypes.PAYTABLE_SHOW_PAYOUTS },
        { name: BaseConfig.regulationTypes.HELP_TABLE_CONTROLS },
    ]

    static nlRegulation = [
        { name: BaseConfig.regulationTypes.DEFAULT_STAKE },
        { name: BaseConfig.regulationTypes.DRAG },
    ]
    /**
     * Constructor calls the main parse function.
     *
     * @param {string} provider Provider key passed in launch config.
     * @param {string} operator Operator key passed in launch config.
     * @param {object} cfg Config data TODO.
     * @param {object} options Features and attributes provided from external classes.
     * @param {object} options.abTest AbTest instance.
     * @param {object} options.name Config instance name.
     */
    constructor(provider, operator, cfg, options = {}){
        this._cfg = this._preParse(cfg);
        this._operator = operator;
        this._provider = provider;
        this._settings = null;
        this._abTest = options.abTest;
        this._urlParams = get;
        this.name = options.name;

        if (this._cfg.bridge && this._cfg.bridge.postParams && typeof this._cfg.bridge.postParams === 'object') {
            Object.keys(this._cfg.bridge.postParams).forEach(key => {
                this._urlParams[key] = this._cfg.bridge.postParams[key];
            });
        }
    }

    /**
     * The main parse function that set all config properties.
     */
    parse() {
        this._isRTG = (this._cfg.game.namespace === 'com.casino.game');
        this._isCayetano = (this._cfg.game.namespace === 'com.cayetano.game');
        this._isNSG = (this._cfg.game.namespace === 'com.nsg.game');
        this._isNetent = (this._cfg.game.namespace === 'com.netent.game');
        this._isDemo = (this._cfg.server.launchParams && this._cfg.server.launchParams.playMode === 'demo');
        this._isSlot = (this._cfg.game.preconfig.gameType === 'slot');
        this._isTap = (this._cfg.game.preconfig.gameType === 'tap');

        this._isTouch = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        this._isIOS10 = document.querySelector('html').classList.contains('ios10');
        this._brand = this._cfg.server.launchParams.custom && this._cfg.server.launchParams.custom.siteId;

        this._parse();
    }

    /**
     * Helper function that returns a piece of the config based on the "prop" argument.
     *
     * @param {string} prop Config property key.
     *
     * @returns {object} Config object by key.
     */
    get(prop) {
        if (!this._cfg[prop]) console.warn(`[bridge] #BaseConfig: You are trying to access undefined prop ${prop}?!?!?!`);

        return this._cfg[prop];
    }

    /**
     * Stores the data from "settings" response as a class property to be able to use it later.
     *
     * @param {object} settings Settings response data.
     */
    setSettings(settings) {
        this._settings = settings;

        this._parse();
    }

    /**
     * Called after gameInfo request. Set mandatory fields from gameInfo response.
     *
     * @param {object} data GameInfo response data.
     */
    parseLaunch(data) {
        let launcherConfig = data.launcherConfig;

        if (launcherConfig.debug) {
            com.casino.debug = {
                enabled: true
            };
        }

        this._cfg = {
            bundle: launcherConfig.bundle,
            bridge: BridgeParser.parseLaunch(launcherConfig),
            server: ServerParser.parseLaunch(launcherConfig),
            game: GameParser.parseLaunch(launcherConfig),
            bars: BarsParser.parseLaunch(launcherConfig),
            analytics: AnalyticsParser.parseLaunch(launcherConfig),
        };

        if (launcherConfig && launcherConfig.bundle) {
            this._cfg = BundleParser.parseLaunch(launcherConfig, this._cfg);
            if (launcherConfig.params) {
                this._urlParams = { ...this._urlParams, ...launcherConfig.params };
            }
        }
    }

    /**
     * Set gameSkin object data.
     * Set custom fields(used in skin mode, to load the correct skin based on gameId).
     * This config is passed to the Bridge launch method.
     */
    parseSkinConfig({ source, skin, originGameId }) {
        if (skin) {
            this._cfg.game.preconfig = {
                ...this._cfg.game.preconfig,
                cdn: this._cfg.game.preconfig.cdn.replace(originGameId, source),
                skin,
                gameAppId: source,
                mappedSkin: Boolean(skin),
            };

            this._cfg.bars.basePath = this._cfg.bars.basePath.replace(originGameId, source);
        }
    }

    /**
     * Set mandatory custom fields that in case of frontend launching they are not passed by the operator nor backend.
     *
     * @param {object} data Launch data.
     *
     * @returns {object} Parsed config.
     */
    _preParse(data = {}) {

        /*
         * Set custom fields(used to set hard-coded fields in bundle mode)
         * example: data.platform = 'https://gserver-casumo.redtiger.cash/casumo/platform/'
         */

        return data;
    }

    _parse() {
        this._parseAnalytics();
        this._parseRedirects();
        this._parseErrors();
        this._parseBridge();
        this._parseServer();
        // Needed because parseServer set siteId property
        this._brand = this._cfg.server.launchParams.custom && this._cfg.server.launchParams.custom.siteId;
        this._isDemo = (this._cfg.server.launchParams && this._cfg.server.launchParams.playMode === 'demo');
        this._parseBars();
        this._parseGame();
        this._parseGamble();
        this._parseWidgets();
        this._parseJackpot();
        this._parseTournament();
        this._parseBonus();
        this._parseRegulations();

        if (this._cfg.bundle) {
            this._parseBundle();
        }

        if (!this.isRTG) {
            this._override();
        }
    }

    /**
     * Set analytics object data. This config is used by the Stats module.
     */
    _parseAnalytics() {
        this._cfg.analytics = AnalyticsParser.parse(this._cfg, this._settings, this.options);
    }

    /**
     * Set redirects object data. This config is used by operator's Adapter.
     */
    _parseRedirects() {
        this._cfg.redirects = {
            realityCheckHistory: false,
            realityCheckExit: false,
            lobby: false,
            deposit: false,
        };
    }

    /**
     * Set errors object data. This config is used by bridge to manage error modal features.
     */
    _parseErrors() {
        this._cfg.errors = ErrorsParser.parse(this._cfg, this._settings, this.options);
    }

    /**
     * Set bridge object data. This config is used by bridge features.
     */
    _parseBridge() {
        this._cfg.bridge = BridgeParser.parse(this._cfg, this._settings, this.options);
    }

    /**
     * Set server object data. This config is used by HTTPClient module.
     */
    _parseServer() {
        this._cfg.server = ServerParser.parse(this._cfg, this._settings, this.options);
    }

    /**
     * Set bars object data. This config is passed to the Bars app.
     */
    _parseBars() {
        this._cfg.bars = BarsParser.parse(this._cfg, this._settings, this.options);
    }

    /**
     * Set game object data. This config is passed to the Game.
     */
    _parseGame() {
        this._cfg.game = GameParser.parse(this._cfg, this._settings, this.options);
    }

    /**
     * Set gamble object data. This config is passed to the Gamble widget.
     */
    _parseGamble() {
        this._cfg.gamble = GambleParser.parse(this._cfg, this._settings, this.options);

        window.com.casino.piePath = this._cfg.gamble && this._cfg.gamble.basePath;
    }

    /**
     * Set widgets object data. This config is passed to the Widgets Panel app.
     */
    _parseWidgets() {
        this._cfg.widgets = WidgetsParser.parse(this._cfg, this._settings, this.options);
    }

    /**
     * Set jackpot object data. This config is passed to the Jackpot widget.
     */
    _parseJackpot(){
        this._cfg.jackpotPanel = JackpotParser.parse(this._cfg, this._settings, this.options);
        // this._cfg.jackpotWheel = JackpotParser.parseWheel(this._cfg, this._settings, this.options);
    }

    _parseTournament() {
        this._cfg.tournament = TournamentParser.parse(this._cfg, this._settings, this.options);
    }

    /**
     * Set bonus object data. This config is passed to the Bonus widget.
     */
    _parseBonus() {
        this._cfg.bonus = BonusParser.parse(this._cfg, this._settings, this.options);
        this._cfg.bonusWheel = BonusParser.parseWheel(this._cfg, this._settings, this.options);

        window.com.casino.bonusPath = this._cfg.bonus && this._cfg.bonus.basePath;
    }

    /**
     * Set regulations data.
     */
    _parseRegulations() {
        if (this._settings && this._settings.user.country) {
            let country = this._settings.user.country.toUpperCase();
            // country = 'DK';

            this.userSpinDuration = this._settings.user.limits && this._settings.user.limits.spinDuration;

            if (this.userSpinDuration){
                this._setRegulations([
                    { name: BaseConfig.regulationTypes.MIN_ROUND_DURATION, options: { duration: this.userSpinDuration, hasSpinProgress: false } }
                ]);
            }

            const { customLanguage, target } = mappingFactory('customLang', this._operator, this._brand);

            if (customLanguage && target) {
                this._setRegulations([{
                    name: BaseConfig.regulationTypes.CUSTOM_LANGUAGE,
                    options: { lang: customLanguage }
                }]);
            } else {
                this._setRegulations(RegulationsFactory.create(country));
            }
        }

        if (brRegulationBrands.includes(this._brand)) {
            this._setRegulations(BaseConfig.brRegulation);
        }

        if (this._provider !== 'evolution') {
            BaseConfig.deRegulation.push({ name: BaseConfig.regulationTypes.REGULATION_RTP });
        }

        /* Netherlands Regulations */
        const defaultStakeBrands = mappingFactory('defaultStakeBrands');

        if (defaultStakeBrands?.includes(this._brand)) {
            this._setRegulations(BaseConfig.nlRegulation);
            console.log(`Netherlands Regulations are activated for brand: ${this._brand}`);
        }
    }

    /**
     * Overrides config properties when bundle mode is enabled.
     */
    _parseBundle() {
        this._cfg = BundleParser.parse(this._cfg, this._settings, this.options);
    }

    /**
     * Overrides config properties based on vendor namespace. This function is called at the end of config parsing so that it has the highest priority.
     */
    _override() {
        switch (this._cfg.game.namespace) {
        case 'com.cayetano.game':
            this._cfg.bars.skin = 'cayetano';
            this._cfg.bars.options.totalStake = this.isTable;
            this._cfg.bars.options.paid = this.isTable;
            this._cfg.bars.options.hasTurboMode = false;

            if (this._settings) {
                this._cfg.gamble = {
                    enabled: this._settings.game.hasGambleGame
                };
                this._cfg.bonus = {
                    ...this._cfg.bonus,
                    hasGamble: this._settings.game.hasGambleGame
                };
            }

            break;
        case 'com.nsg.game':
            this._cfg.bonus.enabled = false;
            break;
        case 'com.netent.game':

            break;
        }
    }

    /**
     * Returns true if the property has a positive value.
     *
     * @private
     *
     * @param {string} name Prop name.
     * @param {object} params Object with params.
     *
     * @returns {boolean} Validated value.
     */
    _isTrue(name, params) {
        return params[name] === 'true' || params[name] === '1';
    }

    // ------- SETTERS --------

    /**
     * Method which enable and parse regulations.
     *
     * @private
     * @param {Array} regulations Regulation list of methods to call.
     */
    _setRegulations(regulations = []) {
        if (regulations.length) {
            this._cfg.bars.options.hasRegulations = true;
            regulations.forEach((regulation) => this._setRegulation(regulation));
        }
    }

    /**
     * Method which enable a specific regulation.
     *
     * @private
     * @param {object} data Regulation data.
     * @param {string} data.name Regulation name.
     * @param {object} data.options Regulation options.
     * @param {number} data.options.duration The min round duration of the current regulation.
     */
    _setRegulation(data) {
        switch (data.name) {
        case BaseConfig.regulationTypes.PAYTABLE_SHOW_PAYOUTS:
            this._cfg.game.preconfig.paytableShowPayouts = true;
            break;
        case BaseConfig.regulationTypes.REGULATION_RTP:
            this._cfg.bars.options.hasRegulationRtp = true;
            break;
        case BaseConfig.regulationTypes.HELP_TABLE_CONTROLS:
            this._cfg.bars.options.helpTableControls = true;
            break;
        case BaseConfig.regulationTypes.MAX_WIN_PROBABILITY:
            this._provider !== 'evolution' && (this._cfg.bars.options.maxWinProbability = true);
            break;
        case BaseConfig.regulationTypes.DRAG:
            this._isSlot && (this._cfg.game.preconfig.dragEnabled = false);
            break;
        case BaseConfig.regulationTypes.VERSION:
            this._cfg.bars.options.hasVersions = true;
            break;
        case BaseConfig.regulationTypes.HELP:
            this._cfg.bars.options.hasHelp = true;
            break;
        case BaseConfig.regulationTypes.TURBO:
            this._cfg.bars.options.hasTurboMode = false;
            break;
        case BaseConfig.regulationTypes.MIN_ROUND_DURATION:
            let duration = data.options.duration;
            let hasSpinProgress = data.options.hasSpinProgress!==undefined ? data.options.hasSpinProgress : (this._isSlot || this._isTap);

            if (this.userSpinDuration && this.userSpinDuration > data.options.duration) {
                duration = this.userSpinDuration;
            }

            this._cfg.bars.options.minRoundDuration = duration;
            this._cfg.bridge.minRoundDuration = duration;
            this._cfg.game.preconfig.minRoundDuration = duration;
            this._cfg.bridge.hasSpinProgress = hasSpinProgress;
            this._cfg.bars.options.hasSpinProgress = hasSpinProgress;
            break;
        case BaseConfig.regulationTypes.RESTRICT_ROUND_CELEBRATIONS:
            this._cfg.bridge.restrictRoundCelebrations = true;
            break;
        case BaseConfig.regulationTypes.SKIP_ANIMATION:
            this._cfg.bridge.hasAnimationSkip = false;
            this._cfg.bars.options.hasAnimationSkip = false;
            break;
        case BaseConfig.regulationTypes.TURBO_SPIN:
            this._cfg.bars.hasTurboSpin = false;
            break;
        case BaseConfig.regulationTypes.WIN_RATES:
            this._cfg.bars.options.hasWinRates = true;
            break;
        case BaseConfig.regulationTypes.WHEEL_WIN_ONLY:
            if (this._cfg.jackpotWheel) this._cfg.jackpotWheel.wheelWinOnly = true;
            break;
        case BaseConfig.regulationTypes.MAX_MULTIPLIER_TEXT:
            this._cfg.bars.hasMaxMultiplierText = false;
            break;
        case BaseConfig.regulationTypes.PAYOUT_EXAMPLE_TEXT_NO_MULTILINE:
            this._cfg.bars.options.hasPayoutExampleTextNoMultiline = true;
            break;
        case BaseConfig.regulationTypes.GAME_SESSION:
            if (this._urlParams.toggleGameSession !== 'false') {
                this._cfg.bars.options.hasGameSession = true;
            }
            break;
        case BaseConfig.regulationTypes.STOP_AUTOPLAY:
            this._cfg.bars.options.hasAutoplay = false;
            break;
        case BaseConfig.regulationTypes.HAS_STOP:
            this._cfg.bars.options.hasStop = false;
            break;
        case BaseConfig.regulationTypes.HIDE_TURBO_TOOLTIP:
            this._cfg.bars.options.hasTurboTooltip = false;
            break;
        case BaseConfig.regulationTypes.HELP_MAX_MULTIPLIER:
            this._cfg.bars.hasHelpMaxMultiplier = false;
            break;
        case BaseConfig.regulationTypes.FEATURE_BUY_COMFIRMATION:
            this._cfg.bars.options.hasFeatureBuyConfirmation = true;
            break;
        case BaseConfig.regulationTypes.FORCE_RESOLUTION:
            this._cfg.game.preconfig.forceResolution = 'SD';
            break;
        case BaseConfig.regulationTypes.HELP_ADDITIONAL_EXPLANATION:
            this._cfg.bars.options.hasAdditionalExplanation = true;
            break;
        case BaseConfig.regulationTypes.PAYTABLE_DYNAMIC_PAYOUTS:
            this._cfg.game.preconfig.paytableDynamicPayouts = true;
            this._cfg.bars.options.paytableDynamicPayouts = true;
            break;
        case BaseConfig.regulationTypes.MIN_ROUND_DURATION_STRICT:
            this._cfg.game.preconfig.minRoundDurationStrict = true;
            break;
        case BaseConfig.regulationTypes.MAX_MULTIPLIER_WIN_LINES:
            this._cfg.bars.options.hasMaxMultiplierWinLines = true;
            break;
        case BaseConfig.regulationTypes.CUSTOM_LANGUAGE:
            this._cfg.game.preconfig.customLang = data.options.lang;
            this._cfg.bars.options.customLang = { enabled: true, lang: data.options.lang };
            break;
        case BaseConfig.regulationTypes.FEATURE_BUY_VERSION:
            this._cfg.bars.options.hasFeatureBuyVersion = true;
            break;
        case BaseConfig.regulationTypes.VOLATILITY_INDEX:
            this._cfg.bars.options.hasVolatilityIndex = true;
            break;
        case BaseConfig.regulationTypes.DOUBLE_REBET_PROMPT:
            this._cfg.game.preconfig.doubleRebetPrompt = true;
            break;
        case BaseConfig.regulationTypes.DEFAULT_STAKE:
            this._cfg.bars.useDefaultStake = true;
            break;
        default:
            console.warn('this type of regulation is not handled yet!');
        }
    }

    /**
     * Set tournament skin in config.
     *
     * @param {object} data Tournament data.
     * @param {string} data.skin Skin name.
     * @private
     *
     * @returns {boolean} Returns with warning if there is no skin parameter passed.
     */
    _setTournament({ skin }) {
        if (!this._isSlot) return console.warn('[bridge] #BaseConfig: You\'re trying to enable tournament while not in slot game!!!');
        if (this._isDemo) return console.warn('[bridge] #BaseConfig: You\'re trying to enable tournament in demo mode!!!');
        if (!skin) return console.warn('[bridge] #BaseConfig: You\'re trying to set a tournament config without skin!!!');
        if (this._isNetent || this._isNSG) return console.warn('[bridge] #BaseConfig: You\'re trying to set a tournament on netent or nsg!!!');

        this._cfg.widgets.enabled = true;

        if (skin === 'tournaments-generic') {
            skin = TournamentSkinMapping.genericSkinMapping();
        }

        // if (asiaSkinMapping.includes(this._operator)) {
        //     skin = 'tournaments-generic'; // TODO: change this to asia skin when skin is ready
        // }

        let tournamentBasePath = `${this._cfg.game.preconfig.cdn}../../widgets/${skin}/`;

        const isInReview = get['inReview']==='true' || this._cfg.bridge.inReview;
        if (isInReview) {
            const tournamentBranch = get['tournamentBranch'] || 'dev';
            tournamentBasePath = `${this._cfg.game.preconfig.cdn}../../../../../widgets/${skin}/${tournamentBranch}/dist/`;
        }

        this._cfg.widgets.tournaments = {
            enabled: true,
            base: tournamentBasePath,
            assets: 'assets/',
            main: 'app.min.js',
            leaderboardUrl: `${this._cfg.bridge.feedUrl}/tournaments/leaderboard/`,
            timestamp: this._cfg.bridge.timestamp
        };
    }

    /**
     * Set jackpot skin in config.
     *
     * @param {object} data Jackpot data.
     * @param {string} data.skin Skin name.
     * @param {string} data.hasWheel True if jackpot wheel has to be enabled.
     * @private
     *
     * @returns {boolean} Returns with warning if there is no skin parameter passed.
     */
    _setJackpot({ skin, hasWheel = true, wheelWinOnly }) {
        const timestamp = this._cfg.bridge.timestamp;
        const skipJackpotMapping = this._cfg.jackpotPanel && this._cfg.jackpotPanel.skipJackpotMapping;

        // switch network jackpot to temporary holiday skin for all existing configs
        if (skin === 'network' && !skipJackpotMapping) {
            skin = JackpotSkinMapping.networkSkinMapping();
        }

        // https://jira.dopamine.bg/browse/INC-383
        this._cfg.jackpotPanel = {
            enabled: false,
            basePath: `${this._cfg.game.preconfig.cdn}../../widgets/jackpots/base-jackpot/`,
            main: 'app.js',
            styles: 'app.css',
            timestamp,
            skin: 'base-jackpot',
            version: 'jackpotNext',
            hasShuffleAnimation: false,
            hasWinAnimation: false,
            feedUrl: `${this._cfg.bridge.feedUrl}/`,
            assets: 'assets/',
            skipJackpotMapping
        };

        if (!this._isSlot) return console.warn('[bridge] #BaseConfig: You\'re trying to enable jackpot while not in slot game!!!');
        hasWheel = hasWheel && !this._isCayetano;
        const isInReview = get['inReview']==='true' || this._cfg.bridge.inReview;

        if (!skin) return console.warn('[bridge] #BaseConfig: You\'re trying to set a jackpot config without skin!!!');


        // Set this before settings or after that is too late for this prop to be set in the game preconfig
        this._cfg.game.preconfig.skipJackpotWin = hasWheel;

        // activate jackpots only after settings to be able to check if the game has a jackpot configuration
        if (this._settings && this._settings.jackpots && this._settings.jackpots.jackpotId) {
            this._cfg.widgets.enabled = true;
            const winType = hasWheel ? 'WHEEL' : 'GAME';

            const jackpotWheelSkin = (hasWheel && !wheelWinOnly) ? skin : 'base-wheel';

            let jackpotBasePath = `${this._cfg.game.preconfig.cdn}../../widgets/jackpots/${skin}/`;
            let jackpotWheelBasePath = `games/scenes/jackpot-wheels/${jackpotWheelSkin}/`;

            if (isInReview) {
                const jackpotBranch = get['jackpotBranch'] || 'dev';
                const jackpotWheelBranch = get['jackpotWheelBranch'] || 'dev';
                const jackpotPack = get['jackpotPack'];
                jackpotBasePath = `${this._cfg.game.preconfig.cdn}../../../../../widgets/jackpots/${jackpotPack ? `${jackpotPack}/` : ''}${skin}/${jackpotBranch}/dist/`;
                jackpotWheelBasePath = `../../../games/scenes/jackpot-wheels/${jackpotWheelSkin}/${jackpotWheelBranch}/dist/`;
            }

            this._cfg.jackpotPanel = {
                enabled: true,
                basePath: jackpotBasePath,
                main: 'app.js',
                styles: 'app.css',
                timestamp,
                skin,
                skinVersion: this._settings.jackpots.version,
                version: 'jackpotNext',
                hasShuffleAnimation: !hasWheel,
                hasWinAnimation: !hasWheel,
                feedUrl: `${this._cfg.bridge.feedUrl}/`,
                assets: 'assets/',
                winType,
                skipJackpotMapping
            };

            this._cfg.jackpotWheel = {
                enabled: hasWheel,
                basePath: jackpotWheelBasePath,
                cdn: `${this._cfg.game.preconfig.cdn}../../../`,
                skin,
                wheelWinOnly,
                timestamp
            };
        }
    }


    // ------- GETTERS --------

    /**
     * Get Help Config.
     *
     * @param {object} info Game info from GameAPI.getGameInfo().
     *
     * @returns {object} Returns parsed help config object.
     */
    getHelp(info) {
        let settings = this._settings || {};
        let jackpots = {};

        const help = info.help;
        const bridge = this._cfg.bridge;
        const bars = this._cfg.bars;

        const hasGamble = this._cfg.gamble && this._cfg.gamble.enabled===true;
        const hasSlotText = this._cfg.game.preconfig.gameType === 'slot';
        const hasGenericText = !(this._cfg.game.namespace === 'com.cayetano.game');
        const helpURL = info.helpURL;
        const rtp = info.rtp;
        const isStateful = info.isStateful;
        const hasRtp = (bars.options.hasRtp !== false && (rtp!==0)); // When rtp is 0 and hasRtp = true bars throws errors
        const gameType = settings.game && settings.game.gameType;
        const maxMultiplier = settings.game && (gameType === 'slot' || gameType === 'scratch')  && bars.hasMaxMultiplierText !== false && settings.game.maxMultiplier;
        const pendingRoundDays = settings.game && settings.game.pendingRoundDays;
        const hasVersions = bars.options.hasVersions;
        const hasFeatureBuyVersion = bars.options.hasFeatureBuyVersion;
        const hasGameSession = bars.options.hasGameSession;

        const hasDynamicTimeJackpotLegacy = bars.help && bars.help.jackpots && (bars.help.jackpots.hasDynamicTimeJackpot === true);
        const dynamicTimeJackpotValueLegacy = bars.help && bars.help.jackpots && bars.help.jackpots.dynamicTimeJackpotValue;

        const hasDynamicTimeJackpot =  settings.jackpots && (settings.jackpots.dynamicAllocation === true);
        const dynamicTimeJackpotValue =  hasDynamicTimeJackpot && settings.jackpots.stakeCaps;
        const hasPayoutExampleTextNoMultiline = bars.options.hasPayoutExampleTextNoMultiline || false;

        const rtpTable = (this._cfg.game.namespace === 'com.casino.game') && (this._cfg.game.preconfig.gameType === 'blackjack' || this._cfg.game.preconfig.gameType === 'roulette' );

        const hasFeatureBuy = bars.options.hasFeatureBuy;

        const hasAdditionalExplanation = bars.options.hasAdditionalExplanation;

        const volatilityIndex = bars.options.hasVolatilityIndex  && settings.game && settings.game.volatilityIndex;

        let maxWinlineHitRate;
        let maxMultiplierHitRate;
        let avgPayoutRate;
        if (bars.options.hasWinRates) {
            maxWinlineHitRate = settings.game && settings.game.maxWinlineHitRate && !isNaN(settings.game.maxWinlineHitRate) ? settings.game.maxWinlineHitRate : null;
            maxMultiplierHitRate = settings.game && settings.game.maxMultiplierHitRate && !isNaN(settings.game.maxMultiplierHitRate) ? settings.game.maxMultiplierHitRate : null;
            avgPayoutRate = hasRtp;
        }

        const stakeSteps = settings.user && settings.user.stakes && settings.user.stakes.types;

        if (settings.jackpots && settings.jackpots.pots && settings.jackpots.pots.length) {
            const _hasPotType = (pots, type) => {
                return Boolean(pots.find((pot) => pot.type === type));
            };
            jackpots = {
                pots: settings.jackpots.pots.map((pot) => {
                    return {
                        name: pot.name,
                        id: pot.id,
                        seedAmount: pot.seedAmount,
                        seedNextAmount: pot.seedNextAmount,
                        type: pot.type
                    };
                }),
                hasTimeJackpot: _hasPotType(settings.jackpots.pots, 'time'),
                hasFixedJackpot: _hasPotType(settings.jackpots.pots, 'fixed'),
                hasCeilingJackpot: _hasPotType(settings.jackpots.pots, 'ceiling'),
                hasProgressiveJackpot: _hasPotType(settings.jackpots.pots, 'progressive'),
                hasDynamicTimeJackpot: hasDynamicTimeJackpot || hasDynamicTimeJackpotLegacy,
                dynamicTimeJackpotValue: dynamicTimeJackpotValue || dynamicTimeJackpotValueLegacy,
                wheelWinOnly: this._cfg.jackpotWheel && this._cfg.jackpotWheel.wheelWinOnly
            };
        }

        const hasAutoplaySettings = (help && help.hasAutoplaySettings !== undefined) ?
            info.hasAutoplaySettings :
            (!bars.options.autoPlayFields) || // by default if there are no configuration we should display all fields
            (bars.options.autoPlayFields && bars.options.autoPlayFields.singleWinLimit) ||
            (bars.options.autoPlayFields && bars.options.autoPlayFields.stopOnJackpot) ||
            (bars.options.autoPlayFields && bars.options.autoPlayFields.stopOnBonus);

        let options = {
            hasAutoplay: (help && help.hasAutoplay !== undefined) ? help.hasAutoplay : (info.hasAutoplay !== false),
            hasStake: (help && help.hasStake !== undefined) ? help.hasStake : (info.hasStake !== false),
            hasGamble: (help && help.hasGamble !== undefined && hasGamble === true) ? help.hasGamble : hasGamble,
            gambleType: this._cfg.gamble && this._cfg.gamble.type,
            hasTurboSpin: (help && help.hasTurboSpin !== undefined) ? help.hasTurboSpin : (info.hasTurboSpin !== false),
            hasJackpotPanel: (help && help.hasJackpotPanel !== undefined) ? help.hasJackpotPanel : this._cfg.bars.options.hasJackpotPanel,
            hasInternalJackpot: (help && help.hasInternalJackpot !== undefined) ? help.hasInternalJackpot : (info.hasInternalJackpot === true),
            hasFreeSpins: (help && help.hasFreeSpins !== undefined) ? help.hasFreeSpins : (info.hasFreeSpins === true),
            hasGameSession: (help && help.hasGameSession !== undefined) ? help.hasGameSession : (this._cfg.bars.options.hasGameSession === true)
        };

        if (bridge.hasAnimationSkip === false || bars.hasTurboSpin === false || bridge.minRoundDuration > 0) {
            options.hasTurboSpin = false;
        }

        const gameVersionsCountries = ['CA']; /* show only game version in help */
        const country = this.settings && this.settings.user && typeof this.settings.user.country === 'string' && this.settings.user.country.split('-')[0];

        let versions = {
            game: settings.game && settings.game.version,
            launcher: gameVersionsCountries.includes(country) ? null : settings.launcher && settings.launcher.version || settings.platform && settings.platform.version,
            manufacturer: gameVersionsCountries.includes(country),
            featureBuy: settings.game && settings.game.featureBuyVersion
        };

        const helpTableControls = bars.options.helpTableControls;

        return {
            ...options,
            hasAutoplaySettings,
            jackpots,
            hasSlotText,
            hasGenericText,
            helpURL,
            rtp,
            hasRtp,
            maxMultiplier,
            gameType,
            pendingRoundDays,
            versions,
            hasVersions,
            hasFeatureBuyVersion,
            isStateful,
            maxWinlineHitRate,
            maxMultiplierHitRate,
            avgPayoutRate,
            hasPayoutExampleTextNoMultiline,
            hasGameSession,
            hasFeatureBuy,
            rtpTable,
            stakeSteps,
            hasAdditionalExplanation,
            volatilityIndex,
            helpTableControls,
        };
    }

    /**
     * Get Game Preloader Config.
     *
     * @param {object} options Game info from GameAPI.getGameInfo().
     *
     * @returns {object} Returns parsed help config object.
     */
    getPreloaderOptions (options = {}) {
        const preloaderValues = {
            client: options.client,
            powered: options.powered,
            studio: options.studio,
            publisher: options.publisher,
            collaborator: options.collaborator,
            license: options.license
        };
        const preloadStyles = {
            background: options.background || '#000000',
        };

        return {
            values: preloaderValues,
            styles: preloadStyles
        };
    }

    _getEvoPreloaderOptions(options = {}) {
        if (options.values.client === 'netent'){
            options.values.collaborator = 'netent';
        }

        if (options.values.powered !== 'fashiontv') {
            options.values.powered = null;
        }

        options.values.client = 'red-tiger';

        options.additionalOptions = {
            bottomText: 'Powered by Evolution',
        };

        return options;
    }

    get options() {
        return {
            namespace: this._cfg.game.namespace,
            isDemo: this.isDemo,
            isSlot: this.isSlot,
            isTap: this.isTap,
            isTable: this.isTable,
            isRTG: this.isRTG,
            isCayetano: this.isCayetano,
            isNetent: this.isNetent,
            isNsg: this.isNsg,
            isIOS10: this._isIOS10,
            urlParams: this._urlParams,
            abTest: this._abTest,
            isDevEnvironment: this.isDevEnvironment
        };
    }

    get settings() {
        return this._settings;
    }

    get isDemo() {
        return this._isDemo;
    }

    get isRTG() {
        return this._isRTG;
    }

    get isCayetano() {
        return this._isCayetano;
    }

    get isNsg() {
        return this._isNSG;
    }

    get isNetent() {
        return this._isNetent;
    }

    get isSlot() {
        return this._isSlot;
    }

    get isTap() {
        return this._isTap;
    }

    get isBlackJack() {
        return (this._cfg.game.preconfig.gameType === 'blackjack');
    }

    get isRoulette() {
        return (this._cfg.game.preconfig.gameType === 'roulette');
    }

    get isBaccarat() {
        return (this._cfg.game.preconfig.gameType === 'baccarat');
    }

    get isTable() {
        return (this._cfg.game.preconfig.gameType === 'blackjack' ||
                this._cfg.game.preconfig.gameType === 'roulette' ||
                this._cfg.game.preconfig.gameType === 'threecardbrag' ||
                this._cfg.game.preconfig.gameType === 'baccarat'
        );
    }

    get isScratch() {
        return this._cfg.game.preconfig.gameType === 'scratch';
    }

    get isDevEnvironment() {
        return (com.casino && com.casino.debug!==undefined);
    }

    get raw() {
        return this._cfg;
    }

}


module.exports = BaseConfig;
